<script setup lang="ts">
import type { ServicesTotals } from '@/composables/jobEdit/useJobEditProductsServices';
import type { SectionId } from '@/composables/jobEdit/useJobEditSectionsOrder';
import type {
  CustomerItem,
  JobEditProductItem,
  JobEditServiceItem,
  LegData,
} from '@/models/jobEdit';

import { computed, onMounted, onUnmounted, provide, ref, watch } from 'vue';
import Sortable from 'sortablejs';

import JobEditPageHeader from '@/components/headers/JobEditPageHeader.vue';
import SimpleLoader from '@/components/loaders/SimpleLoader.vue';
import JobEditOverlay from '@/components/overlays/JobEditOverlay.vue';
import JobEditCustomerSection from '@/components/sections/JobEditCustomerSection.vue';
import JobEditOtherSection from '@/components/sections/JobEditOtherSection.vue';
import JobEditPhotosSection from '@/components/sections/JobEditPhotosSection.vue';
import JobEditProductsServicesSection from '@/components/sections/JobEditProductsServicesSection.vue';
import JobEditStatusSection from '@/components/sections/JobEditStatusSection.vue';
import JobEditStopsSection from '@/components/sections/JobEditStopsSection.vue';
import { useJobEditContentData } from '@/composables/jobEdit/useJobEditContentData';
import { useJobEditLockTimeout } from '@/composables/jobEdit/useJobEditLockTimeout';
import { useJobEditOtherData } from '@/composables/jobEdit/useJobEditOtherData';
import { useJobEditPhotosData } from '@/composables/jobEdit/useJobEditPhotosData';
import {
  DRAG_HANDLE_CLASS,
  IsDraggingKey,
  useJobEditSectionsOrder,
} from '@/composables/jobEdit/useJobEditSectionsOrder';
import { useJobEditStore } from '@/stores/useJobEditStore';
import {
  adaptProductFromApi,
  adaptServiceFromApi,
} from '@/helpers/adapters/jobEditProductsServicesAdapters';
import { fetchPutJobCanSchedule } from '@/helpers/fetchers';

export interface Props {
  showCloseBtn?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  showCloseBtn: false,
});

const emit = defineEmits<{
  closeBtnClick: [event: Event];
}>();

const jobEditStore = useJobEditStore();
const { startTimeout, clearTimeout } = useJobEditLockTimeout();

const {
  stopImages,
  isFetchingPhotos,
  fetchStopImages,
  handleDeleteImage,
  handleSubmitPhotos,
} = useJobEditPhotosData();

const {
  isStaff,
  attachments,
  queriesTableData,
  ticketsTableData,
  isFetchingTickets,
  isFetchingAttachments,
  fetchTickets,
  fetchCustomerOpsEmails,
  fetchChannelsEmails,
  handleNewMessage,
  handleNewTicket,
  handleSubmitAttachments,
} = useJobEditOtherData();

const { isHoldLoading, handleToggleHold } = useJobEditContentData({
  fetchTickets,
  fetchStopImages,
  fetchCustomerOpsEmails,
  fetchChannelsEmails,
});

const { order, setOrder } = useJobEditSectionsOrder();

const sectionsContainer = ref<HTMLElement | null>(null);
const pageHeaderEl = ref<InstanceType<typeof JobEditPageHeader> | null>(null);
const pageHeaderHeight = ref(0);
let sortable: Sortable | null = null;
let headerResizeObserver: ResizeObserver | null = null;

const isDragging = ref(false);
provide(IsDraggingKey, isDragging);

const activeFeedbackSection = ref<string | null>(null);
const feedbackTimeoutId = ref<number | null>(null);

watch(
  () => jobEditStore.lockState.isLocked,
  (isLocked) => {
    if (isLocked) {
      startTimeout();
    } else {
      clearTimeout();
    }
    sortable?.option('disabled', isLocked);
  },
);

const stops = computed(() => jobEditStore.jobData?.stops || []);

const legs = computed<LegData[]>(() => {
  return stops.value.slice(0, -1).map((stop, index) => {
    const products: JobEditProductItem[] = Object.values(stop.pickup_products)
      .slice()
      .sort(
        (a, b) =>
          (a.line_number ?? Number.MAX_SAFE_INTEGER) -
          (b.line_number ?? Number.MAX_SAFE_INTEGER),
      )
      .map(adaptProductFromApi);
    const services: JobEditServiceItem[] =
      stop.services.map(adaptServiceFromApi);
    return {
      stopNumber: stop.stop_number,
      pickupStop: stop.stop_number,
      deliveryStop: stops.value[index + 1].stop_number,
      products,
      services,
      instructions: stop.instructions || '',
    };
  });
});

const legTotals = ref<Record<number, ServicesTotals>>({});

const jobTotal = computed<ServicesTotals>(() =>
  Object.values(legTotals.value).reduce(
    (acc, t) => ({
      quantity: acc.quantity + t.quantity,
      scheduledFee: acc.scheduledFee + t.scheduledFee,
      fee: acc.fee + t.fee,
      gst: acc.gst + t.gst,
      secondQuantity: acc.secondQuantity + t.secondQuantity,
      pay: acc.pay + t.pay,
    }),
    { quantity: 0, scheduledFee: 0, fee: 0, gst: 0, secondQuantity: 0, pay: 0 },
  ),
);

function handleLegStopTotalUpdate(
  stopNumber: number,
  total: ServicesTotals,
): void {
  legTotals.value = { ...legTotals.value, [stopNumber]: total };
}

const currentJobKey = computed(
  () =>
    `${jobEditStore.jobData?.rockhopper_id}-${jobEditStore.jobData?.rockhopper_date}`,
);

const customerData = computed<CustomerItem>(() => ({
  customer_code: jobEditStore.jobData?.customer || '',
  caller: jobEditStore.jobData?.caller || '',
  phone: jobEditStore.jobData?.phone || '',
  reference_1: jobEditStore.jobData?.reference_1 || '',
  reference_2: jobEditStore.jobData?.reference_2 || '',
  reference_3: jobEditStore.jobData?.reference_3 || '',
  reference_4: jobEditStore.jobData?.reference_4 || '',
}));

const isCanScheduleLoading = ref(false);

async function handleCanScheduleToggle(value: boolean): Promise<void> {
  if (isCanScheduleLoading.value) return;
  const jobId = jobEditStore.jobData?.rockhopper_id;
  const jobDate = jobEditStore.jobData?.rockhopper_date;
  if (!jobId || !jobDate) return;
  isCanScheduleLoading.value = true;
  try {
    await fetchPutJobCanSchedule({
      job_id: jobId,
      job_date: jobDate,
      can_schedule: value,
    });
    jobEditStore.setCanSchedule(value);
  } catch {
    // error notification is shown by the fetcher; no state change on failure
  } finally {
    isCanScheduleLoading.value = false;
  }
}

const headerData = computed(() => ({
  jobId: jobEditStore.jobData?.rockhopper_id,
  jobDate: jobEditStore.jobData?.rockhopper_date,
  customerCode: jobEditStore.jobData?.customer,
  status: jobEditStore.jobData?.status,
  pickingStatus: jobEditStore.jobData?.picking_status,
  bookedBy: jobEditStore.jobData?.booked_by,
  stopsCount: stops.value.length,
  driverCode: jobEditStore.jobData?.driver_code,
  showCloseBtn: props.showCloseBtn,
  scheduleBoard: jobEditStore.jobData?.schedule_board?.name ?? null,
  canSchedule: !!jobEditStore.jobData?.stops[0]?.can_schedule,
  canScheduleLoading: isCanScheduleLoading.value,
  isLocked: jobEditStore.lockState.isLocked,
  lockedBy: jobEditStore.lockState.lockedBy,
  toggleCanSchedule: handleCanScheduleToggle,
}));

watch(
  () => jobEditStore.overlayFeedback.tick,
  () => {
    const sectionName = jobEditStore.overlayFeedback.sectionName;

    if (!sectionName) {
      return;
    }

    if (feedbackTimeoutId.value !== null) {
      window.clearTimeout(feedbackTimeoutId.value);
      feedbackTimeoutId.value = null;
    }

    activeFeedbackSection.value = null;

    requestAnimationFrame(() => {
      activeFeedbackSection.value = sectionName;

      feedbackTimeoutId.value = window.setTimeout(() => {
        activeFeedbackSection.value = null;
        feedbackTimeoutId.value = null;
      }, 200);
    });
  },
);

onMounted(() => {
  const headerRoot = pageHeaderEl.value?.$el;
  if (headerRoot instanceof Element) {
    headerResizeObserver = new ResizeObserver(([entry]) => {
      pageHeaderHeight.value =
        entry.borderBoxSize[0]?.blockSize ?? entry.contentRect.height;
    });
    headerResizeObserver.observe(headerRoot);
  }
});

watch(sectionsContainer, (el) => {
  sortable?.destroy();
  sortable = null;
  if (!el) return;

  sortable = Sortable.create(el, {
    animation: 150,
    handle: `.${DRAG_HANDLE_CLASS}`,
    disabled: jobEditStore.lockState.isLocked,
    onStart() {
      isDragging.value = true;
    },
    onEnd({ oldIndex, newIndex }) {
      isDragging.value = false;
      if (oldIndex == null || newIndex == null || oldIndex === newIndex) return;
      const newOrder = [...order.value];
      const [moved] = newOrder.splice(oldIndex, 1);
      newOrder.splice(newIndex, 0, moved);
      setOrder(newOrder);
    },
  });
});

onUnmounted(() => {
  if (feedbackTimeoutId.value !== null) {
    window.clearTimeout(feedbackTimeoutId.value);
  }
  sortable?.destroy();
  headerResizeObserver?.disconnect();
});

function sectionIdForLockName(lockName: string): SectionId | null {
  if (lockName === 'customer-details') return 'customer';
  if (lockName === 'stops') return 'stops';
  if (lockName.startsWith('products-services-stop-')) return 'productsServices';
  return null;
}

function isSectionShaking(sectionId: SectionId): boolean {
  const lockName = activeFeedbackSection.value;
  if (!lockName) return false;
  return sectionIdForLockName(lockName) === sectionId;
}

function handleCloseBtnClick(event: Event): void {
  emit('closeBtnClick', event);
}
</script>

<template>
  <div
    :class="$style.jobEditContentSection"
    :style="{ '--page-header-height': `${pageHeaderHeight}px` }"
    data-testid="jobEditContentSection"
  >
    <div v-if="!jobEditStore.jobData" :class="$style.loaderContainer">
      <SimpleLoader :size="64" :width="6" />
    </div>
    <template v-else>
      <JobEditPageHeader
        ref="pageHeaderEl"
        v-bind="headerData"
        @close-click="handleCloseBtnClick"
      />

      <div ref="sectionsContainer" :class="$style.sectionsContainer">
        <div
          v-for="sectionId in order"
          :key="sectionId"
          :class="[
            sectionId === 'other' ? $style.otherSection : $style.section,
            { [$style.sectionShake]: isSectionShaking(sectionId) },
          ]"
        >
          <JobEditCustomerSection
            v-if="sectionId === 'customer'"
            :key="`customer-${currentJobKey}`"
            :initial-data="customerData"
          />
          <JobEditStopsSection
            v-else-if="sectionId === 'stops'"
            :stops="stops"
          />
          <JobEditStatusSection
            v-else-if="sectionId === 'status'"
            :payment-status="jobEditStore.jobData.payment_status"
            :invoice-status="jobEditStore.jobData.invoice_status"
            :is-hold-loading="isHoldLoading"
            @toggle-hold="handleToggleHold"
          />
          <JobEditOtherSection
            v-else-if="sectionId === 'other'"
            :job-id="jobEditStore.jobData.rockhopper_id"
            :job-date="jobEditStore.jobData.rockhopper_date"
            :attachments="attachments"
            :queries-data="queriesTableData"
            :tickets-data="ticketsTableData"
            :is-loading-tickets="isFetchingTickets"
            :is-loading-attachments="isFetchingAttachments"
            :is-staff="isStaff"
            :refresh-detailed-job="jobEditStore.loadJob"
            @new-message="handleNewMessage"
            @new-ticket="handleNewTicket"
            @submit-attachments="handleSubmitAttachments"
          />
          <JobEditPhotosSection
            v-else-if="sectionId === 'photos'"
            :stop-images="stopImages"
            :delete-image="handleDeleteImage"
            :is-loading-photos="isFetchingPhotos"
            @submit-photos="handleSubmitPhotos"
          />
          <template v-else-if="sectionId === 'productsServices'">
            <JobEditProductsServicesSection
              v-for="leg in legs"
              :key="`${currentJobKey}-leg-${leg.stopNumber}`"
              :class="$style.leg"
              :stop-number="leg.stopNumber"
              :pickup-stop="leg.pickupStop"
              :delivery-stop="leg.deliveryStop"
              :initial-products="leg.products"
              :initial-services="leg.services"
              :initial-instructions="leg.instructions"
              :job-total="jobTotal"
              @update:stop-total="
                (total) => handleLegStopTotalUpdate(leg.stopNumber, total)
              "
            />
          </template>
        </div>
      </div>

      <JobEditOverlay v-if="jobEditStore.lockState.isCurrentSection" />
    </template>
  </div>
</template>

<style module lang="scss">
@use '@/styles/colors';

.jobEditContentSection {
  position: relative;
}

.sectionShake {
  animation: sectionShake 200ms ease-out;
}

@keyframes sectionShake {
  0% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-6px);
  }
  50% {
    transform: translateX(6px);
  }
  75% {
    transform: translateX(-3px);
  }
  100% {
    transform: translateX(0);
  }
}

.loaderContainer {
  min-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sectionsContainer {
  margin-top: 32px;
}

.section {
  margin: 0 32px 24px 32px;
}

.otherSection {
  margin: 0 32px 32px 32px;
}

.leg:not(:last-child) {
  margin-bottom: 8px;
}
</style>
